import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/format";
import { applyTheme, getTheme } from "@/lib/theme";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [theme, setTheme] = useState(getTheme());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [usernameModal, setUsernameModal] = useState(false);

  const authError = (err) => {
    if (err.response?.status === 429) {
      return "Too many attempts — wait a few minutes before trying again";
    }
    return formatApiError(err.response?.data?.detail) || err.message;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register") {
      if (password.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      setUsernameModal(true);
      return;
    }
    setBusy(true);
    try {
      await login(username, password);
      toast.success("Welcome");
      navigate("/");
    } catch (err) {
      setError(authError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitUsername = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Username is required");
      return;
    }
    setBusy(true);
    try {
      await register(name.trim(), password);
      setUsernameModal(false);
      toast.success("Welcome");
      navigate("/");
    } catch (err) {
      toast.error(authError(err));
    } finally {
      setBusy(false);
    }
  };

  const bypass = async () => {
    setError("");
    setBusy(true);
    try {
      try {
        await login("guest", "guest123");
      } catch (_) {
        await register("guest", "guest123");
      }
      navigate("/");
    } catch (err) {
      setError(authError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    background: "transparent",
    borderBottom: "1px solid var(--border)",
    color: "var(--text-primary)",
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="font-head font-light text-5xl tracking-tighter mb-2" style={{ color: "var(--text-primary)" }}>
          mawilu
        </h1>
        <p className="text-sm mb-14" style={{ color: "var(--text-muted)" }}>
          trading post for biological intelligence
        </p>

        <form onSubmit={submit} className="flex flex-col gap-8">
          {mode === "login" && (
            <input
              data-testid="auth-username-input"
              type="text"
              placeholder="username"
              maxLength={20}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full py-3 text-lg"
              style={inputStyle}
            />
          )}
          <input
            data-testid="auth-password-input"
            type="password"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full py-3 text-lg"
            style={inputStyle}
          />
          {mode === "register" && (
            <input
              data-testid="auth-confirm-password-input"
              type="password"
              placeholder="confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full py-3 text-lg"
              style={inputStyle}
            />
          )}
          {mode === "register" && (
            <p className="text-xs -mt-4" style={{ color: "var(--text-muted)" }} data-testid="auth-password-hint">
              password must be at least 8 characters
            </p>
          )}

          {error && (
            <p data-testid="auth-error" className="text-sm" style={{ color: "var(--accent)" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="btn w-full py-3 mt-4 text-sm tracking-widest"
          >
            {busy ? "…" : mode === "login" ? "ENTER" : "CREATE USERNAME"}
          </button>
        </form>

        <button
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
          data-testid="auth-toggle-btn"
          className="btn w-full py-3 mt-8 text-sm tracking-widest"
        >
          {mode === "login" ? "CREATE ACCOUNT" : "SIGN IN"}
        </button>

        <div className="mt-12 pt-8" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            onClick={bypass}
            disabled={busy}
            data-testid="auth-bypass-btn"
            className="btn w-full py-3 text-sm tracking-widest"
          >
            SKIP LOGIN
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 mt-12" data-testid="theme-selector">
          {[
            { k: "red", label: "R", color: "hsl(0, 95%, 60%)" },
            { k: "green", label: "G", color: "hsl(140, 95%, 58%)" },
            { k: "blue", label: "B", color: "hsl(212, 95%, 60%)" },
          ].map((t) => {
            const active = theme === t.k;
            return (
              <button
                key={t.k}
                data-testid={`theme-btn-${t.k}`}
                onClick={() => { applyTheme(t.k); setTheme(t.k); }}
                className="w-11 h-11 text-sm tracking-widest transition-all duration-200"
                style={{
                  border: `1px solid ${t.color}`,
                  color: active ? "#121212" : t.color,
                  background: active ? t.color : "transparent",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {usernameModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setUsernameModal(false)}
          data-testid="username-modal-overlay"
        >
          <form
            onSubmit={submitUsername}
            className="w-full max-w-md p-8"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(ev) => ev.stopPropagation()}
            data-testid="username-modal"
          >
            <div className="flex justify-between items-start mb-6">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Username</span>
              <button type="button" onClick={() => setUsernameModal(false)} data-testid="username-modal-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>
                [ close ]
              </button>
            </div>

            <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-secondary)" }} data-testid="username-disclaimer">
              we urge you to choose a username that is not your real name... you cannot change your username after this
            </p>

            <input
              data-testid="auth-name-input"
              placeholder="username"
              maxLength={20}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              className="w-full py-3 text-lg"
              style={inputStyle}
            />

            <button
              type="submit"
              disabled={busy}
              data-testid="username-submit-btn"
              className="btn w-full py-3 mt-8 text-sm tracking-widest"
            >
              {busy ? "…" : "SUBMIT"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
